import Layout from "../components/Layout";
import Seo from "../components/Seo";

function PageShell({ seo, title, children }) {
  return (
    <Layout>
      {seo}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <h1 className="font-oswald text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 mb-6">{title}</h1>
        <div className="space-y-4 text-slate-600 leading-relaxed text-[15px]">{children}</div>
      </div>
    </Layout>
  );
}

export function AboutUs() {
  return (
    <PageShell
      title="About Us"
      seo={
        <Seo
          title="About Us | Night Teer Result Today"
          description="Learn about Night Teer Result Today, an independent informational website publishing daily Night Teer FR and SR results, common numbers and previous results."
          path="/about-us"
        />
      }
    >
      <p>
        Night Teer Result Today is an independent informational website dedicated to publishing Night Teer results in a
        fast, clean and mobile-friendly format. Our aim is to help visitors check today's First Round (FR) and Second
        Round (SR) results quickly, without clutter or slow-loading pages.
      </p>
      <p>
        We also maintain a Common Number section and a full archive of previous results so that visitors can easily
        review past outcomes. Every result published on our site is stored automatically, ensuring the previous result
        history always stays accurate and up to date.
      </p>
      <p>
        Please note that we are strictly an informational platform. We do not organise, run or promote any game. All
        content, including common numbers, is provided purely for informational and entertainment purposes.
      </p>
    </PageShell>
  );
}

export function ContactUs() {
  return (
    <PageShell
      title="Contact Us"
      seo={
        <Seo
          title="Contact Us | Night Teer Result Today"
          description="Get in touch with Night Teer Result Today for questions, feedback or corrections regarding published results and content."
          path="/contact-us"
        />
      }
    >
      <p>
        We value your feedback. If you have a question, a suggestion, or you notice an error in a published result,
        please reach out and we will do our best to respond.
      </p>
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Email</p>
        <a href="mailto:contact@nightteerresulttoday.com" className="text-red-600 font-semibold text-lg" data-testid="contact-email">
          contact@nightteerresulttoday.com
        </a>
      </div>
      <p>
        For content corrections, kindly mention the specific date and section (Result or Common Number) so we can review
        and update it promptly.
      </p>
    </PageShell>
  );
}

export function PrivacyPolicy() {
  return (
    <PageShell
      title="Privacy Policy"
      seo={
        <Seo
          title="Privacy Policy | Night Teer Result Today"
          description="Read the Privacy Policy of Night Teer Result Today to understand how we handle information on our website."
          path="/privacy-policy"
        />
      }
    >
      <p>
        This Privacy Policy explains how Night Teer Result Today handles information when you visit our website. We keep
        things simple: our site is primarily an informational resource and does not require you to create an account or
        submit personal details to view results.
      </p>
      <p>
        <strong>Information we collect:</strong> We do not collect personally identifiable information from visitors of
        the public pages. Standard, non-identifying technical data such as browser type and general usage may be
        processed by hosting or analytics services to keep the site running smoothly.
      </p>
      <p>
        <strong>Cookies:</strong> The public website does not rely on tracking cookies to display results. Any
        third-party services we may use in future would be governed by their own privacy policies.
      </p>
      <p>
        <strong>Data security:</strong> Administrative access to the website is protected by secure authentication.
        Sensitive configuration and credentials are never exposed publicly.
      </p>
      <p>
        By using this website, you agree to this Privacy Policy. We may update it from time to time, and any changes
        will be reflected on this page.
      </p>
    </PageShell>
  );
}

export function TermsAndConditions() {
  return (
    <PageShell
      title="Terms & Conditions"
      seo={
        <Seo
          title="Terms & Conditions | Night Teer Result Today"
          description="Review the Terms & Conditions for using Night Teer Result Today, an informational results website."
          path="/terms-and-conditions"
        />
      }
    >
      <p>
        By accessing and using Night Teer Result Today, you agree to these Terms & Conditions. If you do not agree,
        please do not use this website.
      </p>
      <p>
        <strong>Informational use only:</strong> All content on this website, including results and common numbers, is
        provided for informational and entertainment purposes only. We make no guarantee regarding the accuracy,
        completeness or timeliness of any information.
      </p>
      <p>
        <strong>No guarantees:</strong> We do not offer or imply any guaranteed winning numbers or predictions. Any
        decisions you make based on the information on this site are entirely your own responsibility.
      </p>
      <p>
        <strong>Legal responsibility:</strong> You are solely responsible for understanding and complying with the laws
        applicable in your location. We are not liable for any loss or consequence arising from the use of this website.
      </p>
      <p>
        We reserve the right to modify or discontinue any part of the website at any time without prior notice.
      </p>
    </PageShell>
  );
}

export function Disclaimer() {
  return (
    <PageShell
      title="Disclaimer"
      seo={
        <Seo
          title="Disclaimer | Night Teer Result Today"
          description="Read the disclaimer for Night Teer Result Today. This is an informational website only and does not guarantee any winning numbers."
          path="/disclaimer"
        />
      }
    >
      <p>
        Night Teer Result Today is an informational website. The information published here, including results and
        common numbers, is shared strictly for informational and entertainment purposes.
      </p>
      <p>
        We do not guarantee any predictions or winning numbers. Common numbers and related number information should not
        be treated as advice or a promise of any outcome.
      </p>
      <p>
        This website does not organise, host, promote or facilitate any game or activity. We are not affiliated with any
        official Teer organisation.
      </p>
      <p>
        Users are fully responsible for understanding and following the laws applicable in their own region or country.
        By using this website, you acknowledge and accept this disclaimer in full.
      </p>
    </PageShell>
  );
}
